// Copyright 2016 Sorama B.V.

import { Disposable } from "aurelia-framework";
import { Point2D, Point3f } from "datatypes/SoramaLocalApi";
import { URA, URAProperties } from "libraries/Math/URA";
import { SerializedChannelSelection } from "services/Hardware/Datatypes/MeasurementDeviceSetup";

export class ChannelSelection {
    // Unique identifier for this preset
    public id: string;

    // Name of this preset
    public name = "";

    // If true, only one channel can be selected at a time
    public singleSelection = false;

    public get count(): number {
        return this._selected.length;
    }

    public get selectedCount(): number {
        return this._selected.filter((x) => x).length;
    }

    // List of selected flags (same length as 'channels')
    public _selected: boolean[] = [];

    private changeObservers: ((index: number, selected: boolean) => void)[] = [];

    constructor(selected?: boolean[], singleSelection?: boolean) {
        this._selected = selected ? selected : [];
        this.singleSelection = singleSelection ? singleSelection : false;
    }

    public changeObserver(onChange: (index: number, selected: boolean) => void): Disposable {
        this.changeObservers.push(onChange);

        return {
            dispose: () => {
                const index = this.changeObservers.findIndex((x) => x === onChange);
                if (index === -1) return;

                this.changeObservers.splice(index, 1);
            },
        };
    }

    public getURAProperties(sensorPositions: Point3f[]): URAProperties {
        if (sensorPositions.length != this._selected.length)
            throw new Error("Number of sensor positions does not match length of current channel selection.");

        const zSensorsPositions = sensorPositions.map((x) => x.Z);
        if (zSensorsPositions.some((z) => Math.abs(z - zSensorsPositions[0]) > Number.EPSILON))
            throw new Error("Not all sensor positions are in the same plane.");

        // Create an array of positions
        const positions: Point2D[] = [];
        for (let i = 0; i < this._selected.length; i++) {
            if (this._selected[i]) positions.push({ x: sensorPositions[i].X, y: sensorPositions[i].Y });
        }

        return URA.getUraProperties(positions);
    }

    public getChannelSelectionIndexes(selection: ChannelSelection) {
        return selection
            .getSelection()
            .map((selected, i) => (selected ? i : undefined))
            .filter((i) => i !== undefined) as number[];
    }

    public toggle(index: number): boolean {
        if (index > this._selected.length || index < 0) {
            throw Error("Channel selection index is out of range");
        }

        if (this.singleSelection) {
            this.deselectAll();
            this._selected[index] = true;
        } else {
            this._selected[index] = !this._selected[index];
        }

        for (const observer of this.changeObservers) {
            observer(index, this._selected[index]);
        }

        return this._selected[index];
    }

    public select(index: number): void {
        if (index > this._selected.length || index < 0) {
            throw Error("Channel selection index is out of range");
        }

        if (!this._selected[index]) {
            if (this.singleSelection) {
                for (let i = 0; i < this._selected.length; i++) {
                    this._selected[i] = false;
                }
            }
            this._selected[index] = true;

            for (const observer of this.changeObservers) {
                observer(index, true);
            }
        }
    }

    public deselect(index: number): void {
        if (index > this._selected.length || index < 0) {
            throw Error("Channel selection index is out of range");
        }

        if (this._selected[index]) {
            this._selected[index] = false;

            for (const observer of this.changeObservers) {
                observer(index, false);
            }
        }
    }

    public deselectAll(): void {
        this._selected.forEach((_, i) => {
            this.deselect(i);
        });
    }
    public selected(index: number): boolean {
        return this._selected[index];
    }

    public getSelection(): boolean[] {
        // Return a copy of the internal array
        return this._selected.map((x) => x);
    }

    public static fromSelectionString(selection: string): ChannelSelection {
        const channelSelection = new ChannelSelection();
        for (let i = 0; i < selection.length; i++) {
            channelSelection._selected.push(selection[i] === "1" ? true : false);
        }

        return channelSelection;
    }

    public static toSelectionString(selection: ChannelSelection): string {
        let str = "";
        for (let i = 0; i < selection._selected.length; i++) {
            str += selection._selected[i] ? "1" : "0";
        }

        return str;
    }

    public static copy(databaseObject: ChannelSelection | SerializedChannelSelection): ChannelSelection {
        if (!databaseObject) return databaseObject as ChannelSelection;

        // If we receive a real ChannelSelection instance, use its API to get a safe copy
        if (databaseObject instanceof ChannelSelection) {
            const selected = (
                databaseObject.getSelection ? databaseObject.getSelection() : databaseObject._selected || []
            ).map((s) => !!s);
            const instance = new ChannelSelection(selected, !!databaseObject.singleSelection);
            instance.id = databaseObject.id;
            instance.name = databaseObject.name;
            // Do not copy changeObservers - those are runtime objects
            return instance;
        }

        // Otherwise treat it as the serialized/plain text shape
        const selected = Array.isArray(databaseObject._selected) ? databaseObject._selected.map((s) => !!s) : [];
        const instantiated = new ChannelSelection(selected, !!databaseObject.singleSelection);
        instantiated.id = databaseObject.id;
        instantiated.name = databaseObject.name;
        return instantiated;
    }
}
